<script setup>
import { ref, watch, computed, vShow } from 'vue';
import { useRouter } from 'vue-router';
import NumberModal from '../components/NumberModal.vue';
import Payment from '@/components/Payment.vue';
import axios from 'axios';

import { 
  kBlock,
  kButton
} from 'konsta/vue';

import dateFn from '@/mixins/date'

import { 
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PauseCircleIcon
} from '@heroicons/vue/24/outline'

import {useConfig} from '@/stores/config'
const configStore = useConfig();

import {useBooking} from '@/stores/booking'
const bookingStore = useBooking();

import {useLoader} from '@/stores/loader'
const LoaderStore = useLoader();

import {usePopup} from '@/stores/popup'
const popupStore = usePopup();

const router = useRouter();

const phoneRequest = ref(false)
const status = ref(null)
const appointmentStatus = computed(() => {
  return status.value == 1;
});

const sendData = {
  phone : ''
}

const errors = ref({})

watch(() => bookingStore.appointment, (newAppointment) => {
  if (newAppointment) {
    status.value = newAppointment.status;
  }
}, { immediate: true });

async function deleteAppoiment(){
  LoaderStore.open();
  try{
    const response = await axios.post('/appointment/'+bookingStore.appointment.token+'/deleteAppointment', sendData)

    if (response.data.status == 'success') {
      if(response.data.result === 'cancellation_requested'){
        bookingStore.appointment.cancellation_requested_at = new Date().toISOString();
        bookingStore.appointment.has_pending_cancellation = true;
      }else{
        status.value = 0
      }

      if (phoneRequest.value) {
        phoneRequest.value = false 
      }
    }

  }catch(e){
    if (e.response.data.message.phone.length > 0 && phoneRequest.value){
      errors.value.phone = e.response.data.message.phone[0]
    }
    if (e.response.data.message.phone.length > 0) {
      phoneRequest.value = true
    }
    else throw e
  }

  LoaderStore.close();
}

function checkNumber(number){
  sendData.phone = number
  deleteAppoiment()
}

const show_details = ref(false);

const optional_services_total = computed(() => {
  if (!bookingStore.appointment?.optional_services) return 0
  return bookingStore.appointment.optional_services.reduce((sum, os) => {
    const price = os.billing_type === 'per_day'
      ? os.price_with_tax * (bookingStore.bnb_nights || 1)
      : os.price_with_tax
    return sum + price
  }, 0)
})

const base_price = computed(() => {
  if (!bookingStore.price_with_tax) return 0
  return bookingStore.price_with_tax - optional_services_total.value
})

const show_new_appointment = computed(() => {
  if(!appointmentStatus.value) return true;
  if(bookingStore.appointment?.confirmed_at) return true;
  return false;
})
const show_delete_appointment = computed(() => {
  if(bookingStore.appointment?.has_pending_cancellation) return false;

  if(configStore.config.flag_accept_refund){
    if(appointmentStatus.value) return true;
    return false;
  }

  return (appointmentStatus.value && !bookingStore.is_paid);
})

const total_listino = computed(() =>
  (bookingStore.appointment?.total_service_with_tax ?? 0) + optional_services_total.value
)

const hasDiscount = computed(() => (bookingStore.appointment?.promo_code))

const formatPrice = (value) => {
  const intPart = Math.trunc(value)
  const decPart = (value * 100).toFixed(0).toString().padStart(2, '0').slice(-2)
  return `${intPart}.${decPart} €`
}
</script>


<template>
  <div class="block md:flex md:h-screen">
    <div class="w-full block md:w-2/5 bg-transparent md:bg-gray-100">
      
      <k-block v-if="configStore.config.is_payment_enabled && appointmentStatus">
        
        <h1 v-if="bookingStore.show_payment" class="text-2xl font-bold text-center">Effettua il pagamento</h1>
        <k-block
          v-if="bookingStore.appointment?.has_pending_cancellation"
          class="flex flex-col items-center md:items-center text-gray-700">
          <PauseCircleIcon class="h-10 w-10 text-orange-500"/>
          <h1 class="text-2xl font-bold">Annullamento in attesa di conferma</h1>
        </k-block>

        <k-block
          v-else-if="bookingStore.is_paid"
          class="flex flex-col items-center md:items-center text-gray-700">
          <CheckCircleIcon class="h-10 w-10 text-[#21be5a]"/>
          <h1 class="text-2xl font-bold">Prenotazione confermata</h1>
        </k-block>

        <k-block
          v-else-if="bookingStore.to_confirm || bookingStore.appointment.payment_method === 'bank_wire'"
          class="flex flex-col items-center md:items-center text-gray-700">
          <PauseCircleIcon class="h-10 w-10 text-yellow-500"/>
          <h1 class="text-2xl font-bold">Prenotazione in attesa di conferma</h1>
        </k-block>
      </k-block>

      <k-block v-else>
        <k-block 
          v-if="appointmentStatus"
          class="flex flex-col items-center md:items-center text-gray-700">
          <CheckCircleIcon class="h-10 w-10 text-[#21be5a]"/>
          <h1 class="text-2xl font-bold">Prenotazione confermata</h1>
        </k-block>

        <k-block 
          v-else
          class="flex flex-col items-center md:items-center text-gray-700">
          <XCircleIcon class="h-10 w-10 text-red-500"/>
          <h1 class="text-2xl font-bold">Prenotazione annullata</h1>
        </k-block>

        <k-block v-if="appointmentStatus" class="hidden text-center md:block mt-20">
          <h1 class="text-8xl font-bold">Grazie!</h1>
        </k-block>
      </k-block>
  
    </div>

    <div class="w-full overflow-y-auto">

      <k-block v-if="bookingStore.appointment" strong class="mb-0 mt-0">
        <div class="flex flex-col md:flex-row md:justify-between md:items-center">

          <!-- date + orari: restano affiancati anche su mobile -->
          <div class="flex items-center">

            <div v-if="bookingStore.appointment.is_multi_days || configStore.config.layout === 'layout_b&b'" class="flex items-center gap-3">
              <div class="flex flex-col">
                <div class="text-xs uppercase opacity-65 tracking-wide mb-0.5">Check-in</div>
                <div class="text-sm opacity-80">{{ dateFn.get_day_name(dateFn.get_week_day_from_date(bookingStore.checkin)) }}</div>
                <div class="flex items-end gap-1.5">
                  <div class="text-5xl font-bold leading-none">{{ new Date(bookingStore.checkin).getDate() }}</div>
                  <div class="flex flex-col">
                    <div class="text-lg lowercase">{{ dateFn.get_month_name(new Date(bookingStore.checkin).getMonth()) }}</div>
                    <div class="text-xs opacity-80">{{ new Date(bookingStore.checkin).getFullYear() }}</div>
                  </div>
                </div>
                <!-- <div class="mt-2 self-start text-xs bg-black/10 rounded-full px-2 py-0.5">
                  {{ bookingStore.bnb_nights }} notti
                </div> -->
              </div>

              <div class="flex flex-col border-l border-gray-300 ml-3.5 pl-3.5">
                <div class="text-xs uppercase opacity-65 tracking-wide mb-0.5">Check-out</div>
                <div class="text-sm opacity-80">{{ dateFn.get_day_name(dateFn.get_week_day_from_date(bookingStore.checkout)) }}</div>
                <div class="flex items-end gap-1.5">
                  <div class="text-5xl font-bold leading-none">{{ new Date(bookingStore.checkout).getDate() }}</div>
                  <div class="flex flex-col">
                    <div class="text-lg lowercase">{{ dateFn.get_month_name(new Date(bookingStore.checkout).getMonth()) }}</div>
                    <div class="text-xs opacity-80">{{ new Date(bookingStore.checkout).getFullYear() }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              <div>{{ dateFn.get_day_name(dateFn.get_week_day_from_date(bookingStore.date_object)) }}</div>
              <div class="flex">
                <div class="text-5xl font-bold leading-none">{{ bookingStore.day_formatted }}</div>
                <div class="ml-2">
                  <div class="text-lg lowercase leading-none mb-1">{{ dateFn.get_month_name(bookingStore.month-1) }}</div>
                  <div>{{ bookingStore.year }}</div>
                </div>
              </div>
            </div>

            <!-- orari: solo per standard -->
            <div v-if="!bookingStore.appointment.is_multi_days && configStore.config.layout !== 'layout_b&b'" class="flex items-center font-bold text-lg ml-3">
              <div class="rounded border py-1 px-2 leading-none border-gray-700">
                {{ dateFn.format_time(bookingStore.start) }}
              </div>
              <ChevronRightIcon class="w-4 h-4 mx-1"/>
              <div class="rounded border py-1 px-2 leading-none border-gray-700">
                {{ dateFn.format_time(bookingStore.end + 1) }}
              </div>
            </div>
          </div>

          <div 
            v-if="bookingStore.service_or_bundle?.show_prices && bookingStore.price_with_tax != null"
            class="mt-4 pt-3 border-t border-gray-300 md:mt-0 md:pt-0 md:border-t-0 md:ml-auto md:text-right md:border-l md:pl-3">
            <div 
              v-if="hasDiscount"
              class="text-sm font-normal line-through text-gray-400 leading-none mb-0.5">
              {{ formatPrice(total_listino) }}
            </div>

            <div class="text-lg font-bold leading-none">
              <span class="text-4xl">{{ Math.trunc(bookingStore.price_with_tax) }}</span>
              <span>.{{ (bookingStore.price_with_tax * 100).toFixed(0).toString().padStart(2, '0').slice(-2) }} €</span>
            </div>

            <div 
              v-if="hasDiscount"
              class="mt-1.5 inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs font-normal px-2 py-0.5 rounded-full">
              {{ bookingStore.promo_code }}
            </div>
          </div>
        </div>
      </k-block>

      <k-block v-if="appointmentStatus" inset block class="mb-0 bg-gray-100 py-3">
        <div class="flex justify-between items-center">
          <div>
            <div class="text-sm">Hai prenotato:</div>
            <div class="text-lg font-bold leading-none">{{ bookingStore.service_name }}</div>
            <div v-if="bookingStore.service_team_users.length">con {{ bookingStore.service_team_users.map(u => u.frontend_name).join(', ') }}</div>
          </div>
          <a 
            v-if="
              bookingStore.note 
              || bookingStore.appointment?.optional_services?.length 
              || (bookingStore.service_or_bundle?.show_prices && bookingStore.price_with_tax) 
              || bookingStore.service_team_users.length
            "
            href="#" 
            class="text-gray-600 text-sm flex items-center"
            @click="show_details = !show_details">
            Dettagli
            <ChevronDownIcon 
              :class="[
                'h-4 w-4 ml-1 transition-transform',
                show_details ? 'transform rotate-180' : ''
              ]"/>
          </a>
        </div>

        <div
          v-if="show_details"
          class="mt-3">

          <ul class="divide-y divide-gray-200">
            <li 
              v-if="bookingStore.service_or_bundle?.show_prices && bookingStore.price_with_tax"
              class="py-2">
              <div class="font-bold">Prezzo:</div>
              {{ bookingStore.price_with_tax.toFixed(2) }} €
            </li>

            <li
              v-for="os in bookingStore.appointment.optional_services"
              class="py-2">
              <div class="font-bold">Opzione:</div>
              {{ os.name }}
              <div v-if="os.show_prices" class="font-bold text-primary">
                + {{ (os.billing_type === 'per_day' 
                      ? os.price_with_tax * (bookingStore.bnb_nights || 1) 
                      : os.price_with_tax).toFixed(2) }} €
              </div>
            </li>

            <li
              v-if="bookingStore.note" 
              class="py-2">
              <div class="font-bold">Note:</div>
              {{ bookingStore.note }}
            </li>

            <li
              v-if="bookingStore.appointment?.services?.[0]?.description">
              <div class="font-bold">Informazioni:</div>
              <div
                v-html="bookingStore.appointment?.services?.[0]?.description" 
                class="prose prose-sm mt-1" />
            </li>
          </ul>
        </div>

      </k-block>

      <k-block strong class="mb-0">
        <img 
          v-if="configStore.account.logo_url"
          :src="configStore.account.logo_url" 
          :alt="configStore.account.name"
          class="h-10 w-2/4 object-contain object-left mb-3">

        <div class="text-lg font-bold leading-none">{{ configStore.account.name }}</div>
        <div class="">
          {{ configStore.account.address }}, {{ configStore.account.address_city }} ({{ configStore.account.address_province }}) - {{ configStore.account.address_zip_code }}
        </div>

        <template v-if="bookingStore.appointment?.confirmed_at !== null">
          <div v-if="appointmentStatus" class="text-gray-500 mt-5">
            Questa prenotazione è stata confermata il {{ dateFn.format_datetime(bookingStore.appointment?.confirmed_at) }}
          </div>
          <div v-else class="text-gray-500 mt-5">
            Questa prenotazione è stata annullata il {{ dateFn.format_datetime(bookingStore.appointment?.deleted_at) }}
          </div>
        </template>

        <div v-if="bookingStore.appointment?.has_pending_cancellation" class="text-orange-600 mt-5">
          Hai richiesto l'annullamento di questa prenotazione. Riceverai una conferma appena la richiesta sarà approvata.
        </div>
      </k-block>


      <k-block 
        v-if="configStore.config.booking_info" 
        strong 
        inset 
        class="text-center bg-primary-light text-primary leading-none">
        <InformationCircleIcon class="h-5 w-5 mb-2 mx-auto"/>

        <div v-html="configStore.config.booking_info"/>
        <br/>
        <a 
          v-if="configStore.config.terms_conditions"
          href="#" 
          @click="popupStore.open('terms_conditions')"
          class="font-bold">
          Leggi tutte le condizioni
        </a>
      </k-block>

      <NumberModal
          :error="errors.phone"
          :showOpen = phoneRequest
          :hiddenNumber = bookingStore.appointment?.hidden_contact_number
          @checkNumber = "checkNumber($event)"
          @close = "phoneRequest = false" 
        />
      <hr>

      <k-block>
        <Payment v-if="appointmentStatus && bookingStore.show_payment"
          @success="_=>bookingStore.getBookingConfirmed()"/>
      </k-block>

      <!-- <k-block>
        <div v-if="!bookingStore.show_payment">Grazie</div>
      </k-block> -->

      <k-block class="block md:flex md:flex-row-reverse md:justify-between md:items-center">
        <k-button
          v-if="show_new_appointment"
          @click="bookingStore.newAppointment()"
          large 
          class="!bg-primary !text-white md:w-auto md:px-5">
          Effettua una nuova prenotazione
        </k-button>

        <k-button 
          v-if="show_delete_appointment"
          @click="deleteAppoiment" 
          class="!bg-white !text-red-500 !text-xs mt-5 md:w-auto">
          Annulla prenotazione
        </k-button>
      </k-block>
    </div>
  </div>
</template>


<style lang="scss">

</style>